import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Owns ALL state and side-effects for the Crest Profile onboarding wizard.
 *
 * Why a hook (not a context):
 * - The wizard is a single instance — never two open at once.
 * - State is scoped to the lifetime of the modal — opening/closing should
 *   reset everything cleanly. That's easier to reason about as a local hook
 *   than a global provider.
 *
 * Returns the shape that `OnboardingWizard` and the step panes consume.
 */
export function useOnboardingForm({ open, onClose }) {
  const { user, setUser } = useAuth();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [brandOther, setBrandOther] = useState("");
  const [form, setForm] = useState(() => initialForm(user));

  // Reset wizard state every time it opens.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError(null);
    setForm(initialForm(user));
    setBrandOther("");
  }, [open, user]);

  // List toggle for chip-style multi-selects (style/category/brand lists).
  // Case-insensitive equality so "Burberry" and "burberry" don't double up.
  const toggle = (listKey, value) =>
    setForm((f) => {
      const list = f[listKey] || [];
      const i = list.findIndex((v) => v.toLowerCase() === value.toLowerCase());
      return {
        ...f,
        [listKey]: i >= 0 ? list.filter((_, idx) => idx !== i) : [...list, value],
      };
    });

  // "Add Another Brand" free-text adder on the Brands step.
  const addBrandOther = () => {
    const v = brandOther.trim();
    if (!v) return;
    setForm((f) =>
      f.favorite_brands.some((b) => b.toLowerCase() === v.toLowerCase())
        ? f
        : { ...f, favorite_brands: [...f.favorite_brands, v] },
    );
    setBrandOther("");
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.patch("/auth/me/preferences", { ...form, mark_onboarded: true });
      setUser(data);
      onClose?.({ saved: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Save whatever they have (even if empty fields) and mark them onboarded so
  // the dashboard banner stops nagging. They can finish later on /crest-profile.
  // Even on error we close — we never want to trap a user in the modal.
  const skip = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.patch("/auth/me/preferences", {
        mark_onboarded: true,
        mobile_number: form.mobile_number || "",
      });
      setUser(data);
      onClose?.({ saved: false, skipped: true });
    } catch (_err) {
      onClose?.({ saved: false, skipped: true });
    } finally {
      setBusy(false);
    }
  };

  const headerSubtitle = useMemo(() => SUBTITLES[step] || SUBTITLES.default, [step]);

  return {
    user,
    step, setStep,
    busy,
    error,
    form, setForm,
    brandOther, setBrandOther,
    toggle, addBrandOther,
    save, skip,
    headerSubtitle,
  };
}

function initialForm(user) {
  return {
    mobile_number: user?.mobile_number || user?.phone || "",
    sizing: { ...(user?.sizing || {}) },
    style_preferences: [...(user?.style_preferences || [])],
    favorite_brands: [...(user?.favorite_brands || [])],
    favorite_categories: [...(user?.favorite_categories || [])],
  };
}

const SUBTITLES = {
  0: "A few quick questions tailor your future requests.",
  1: "How can the pro shop reach you for urgent updates?",
  2: "Your sizes — kept private, never shared.",
  3: "Pick the looks you gravitate toward.",
  4: "Which categories interest you most?",
  5: "Which makers do you favor?",
  default: "All set — review and save.",
};
